import React, {
  useEffect,
  useState,
  createContext,
  useContext,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { DocumentNode } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import { isAuthenticated, signOut as signOutApi } from '../lib/auth';
import { MY_USER } from '../lib/graphql/queries/user';
import { User, CurrentUserProps, PermissionAccessMap } from '../types/user';
import { Permission } from '../types/permissionGroup';
import { regularToCamelCase } from '../utils/string';

type NotifyOptions = Record<string, unknown>;

interface AuthContextValue extends CurrentUserProps {
  signOut: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextValue | null>(null);
export const mockReportError = (_err: any, _options: any) => {};

interface AuthProviderProps {
  children: React.ReactNode;
  reportError: (err: any, options: NotifyOptions) => void;
  authEndpoint: string;
  userQuery?: DocumentNode;
}

// BroadcastChannel name for cross-tab communication
const AUTH_BROADCAST_CHANNEL = 'elevator-auth-events';

export const AuthProvider: React.FunctionComponent<AuthProviderProps> = ({
  children,
  reportError,
  authEndpoint,
  userQuery = MY_USER,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [lastAuthenticated, setLastAuthenticated] = useState<Date | null>(null);
  const client = useApolloClient();
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);

  // Initialize BroadcastChannel for cross-tab communication
  useEffect(() => {
    if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
      const channel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
      broadcastChannelRef.current = channel;

      // Listen for logout events from other tabs
      channel.onmessage = (event) => {
        if (event.data === 'logout') {
          // Clear user state and Apollo cache when logout is broadcast from another tab
          setUser(null);
          client.clearStore().catch((err) => {
            if (process.env.NODE_ENV === 'development') {
              console.error('Error clearing Apollo cache:', err);
            }
          });
        }
      };

      return () => {
        channel.close();
        broadcastChannelRef.current = null;
      };
    }
  }, [client]);

  const hydrateUser = useCallback(
    async (useCache = true): Promise<User | null> => {
      let operation = 'isAuthenticated';
      let user: User | null = null;

      try {
        const hasCurrentUser = await isAuthenticated(authEndpoint);

        if (hasCurrentUser) {
          operation = 'query myUser';
          const { data } = (await client.query({
            query: userQuery,
            fetchPolicy: useCache ? 'cache-first' : 'network-only',
          })) as { data: { myUser: User; userPermissions?: Permission[] } };

          user = data.myUser;

          if (data.userPermissions) {
            user = {
              ...data.myUser,
              permissions: data.userPermissions,
            };
          }

          setUser(user);
        } else {
          // Clear user state when not authenticated
          setUser(null);
        }
      } catch (err) {
        reportError(err, {
          metaData: { operation },
        });
        // On error, clear user state to be safe
        setUser(null);
      } finally {
        setLastAuthenticated(new Date());
      }

      return user;
    },
    [authEndpoint, client, reportError, userQuery],
  );

  // Centralized signOut method that handles all cleanup
  const signOut = useCallback(async (): Promise<boolean> => {
    try {
      // Call API to sign out
      const success = await signOutApi(authEndpoint);

      if (success) {
        // Clear user state
        setUser(null);

        // Clear Apollo cache
        await client.clearStore();

        // Broadcast logout event to other tabs
        if (broadcastChannelRef.current) {
          broadcastChannelRef.current.postMessage('logout');
        }
      }

      return success;
    } catch (err) {
      reportError(err, {
        metaData: { operation: 'signOut' },
      });
      // Even on error, clear local state
      setUser(null);
      await client.clearStore();
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.postMessage('logout');
      }
      return false;
    }
  }, [authEndpoint, client, reportError]);

  return (
    <AuthContext.Provider
      value={{ currentUser: user, hydrateUser, lastAuthenticated, signOut }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (useCache = true) => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  const { currentUser, hydrateUser, lastAuthenticated, signOut } = context;

  // Hydrate user on mount
  useEffect(() => {
    hydrateUser(useCache);
  }, [hydrateUser, useCache]);

  // Re-check auth when window regains focus or tab becomes visible
  // This helps detect logout in other tabs
  useEffect(() => {
    const handleFocus = () => {
      // Force network check when window regains focus
      hydrateUser(false);
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Force network check when tab becomes visible
        hydrateUser(false);
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [hydrateUser]);

  const permissions = useMemo(() => {
    return currentUser?.permissions ?? [];
  }, [currentUser]);

  const permissionsMap: PermissionAccessMap = useMemo(() => {
    return permissions.reduce((prev: PermissionAccessMap, current) => {
      const type = regularToCamelCase(current.type);

      if (!prev[type]) prev[type] = { access: false };

      prev[type].access = current.access || prev[type].access;
      prev[type][current.operationName] = current.access;

      return prev;
    }, {});
  }, [permissions]);

  const isTeamMember = () => {
    return currentUser
      ? ['Admin', 'Team'].includes(currentUser.permissionGroup.groupName)
      : false;
  };

  return {
    currentUser,
    hydrateUser,
    signOut,
    lastAuth: lastAuthenticated,
    isTeamMember,
    permissionsMap,
  };
};
